import type { components } from '../../../../../unifiedapi';

type RiskAssessmentType = components['schemas']['RiskAssessmentType'];

/**
 * A supplier risk dimension, mirroring the scoring dimensions defined in Metis
 * (`app/core/scoring/constants.py`). Dimensions are either:
 *
 * - `computed` — derived from alert incidents (findings) whose STIX `types`
 *   map to the dimension (see `EVENT_TYPE_DIMENSION` in Metis).
 * - `manual` — sourced from `CompanyRiskAssessment` records of a given type.
 */
interface BaseRiskDimension {
  /** Stable key used for the tab value / URL state. */
  key: string;
  /** Human-readable tab label. */
  label: string;
}

export interface ComputedRiskDimension extends BaseRiskDimension {
  kind: 'computed';
  /**
   * STIX `EventType` values matched against an alert incident's `types`. A
   * dimension matches an incident that carries any of these types. An empty
   * list has no mapped event types and therefore never matches a finding.
   */
  incidentTypes: readonly string[];
}

export interface ManualRiskDimension extends BaseRiskDimension {
  kind: 'manual';
  /** `CompanyRiskAssessment.type` backing this dimension. */
  assessmentType: RiskAssessmentType;
}

export type SupplierRiskDimension = ComputedRiskDimension | ManualRiskDimension;

/**
 * The six supplier risk dimensions, in Metis weight order. The event-type
 * groupings below replicate Metis' `EVENT_TYPE_DIMENSION` mapping exactly and
 * must be kept in sync with it.
 */
export const SUPPLIER_RISK_DIMENSIONS: ReadonlyArray<SupplierRiskDimension> = [
  {
    key: 'identity',
    label: 'Identity',
    kind: 'computed',
    incidentTypes: [
      'theft-access-credentials',
      'data-exfiltration',
      'unauthorized-access-information',
      'unauthorized-access-system',
      'unauthorized-release',
      'compromised-system',
      'brute-force-attempt',
      'dictionary-attack-attempt',
      'password-cracking-attempt',
      'aggregation-information-phishing-schemes',
      'dissemination-phishing-emails',
      'hosting-phishing-sites',
    ],
  },
  {
    key: 'network',
    label: 'Network',
    kind: 'computed',
    incidentTypes: [
      'dns-cache-poisoning',
      'dns-local-resolver-hijacking',
      'dns-spoofing-registered',
      'dns-spoofing-unregistered',
      'dns-rebinding',
      'dns-server-compromise',
      'dns-stub-resolver-hijacking',
      'dns-zone-transfer',
      'domain-name-compromise',
      'lame-delegations',
    ],
  },
  {
    key: 'underground',
    label: 'Underground',
    kind: 'computed',
    incidentTypes: [
      'dark-web',
      'underground-forum',
      'underground-activity',
      'ransomware',
      'ransomware-payment',
    ],
  },
  {
    key: 'geopolitical',
    label: 'Geopolitical',
    kind: 'computed',
    incidentTypes: [
      'physical-threat',
      'diplomatic-tensions',
      'policy-compliance',
      'trade-agreements',
    ],
  },
  {
    key: 'internal-assessment',
    label: 'Internal Assessment',
    kind: 'manual',
    assessmentType: 'internal_assessment',
  },
  {
    key: 'external-assessment',
    label: 'External Assessment',
    kind: 'manual',
    assessmentType: 'external_assessment',
  },
];

/** Event-type tokens that should stay upper-cased when humanized. */
const EVENT_TYPE_ACRONYMS = new Set(['dns']);

/**
 * Turns a kebab-case STIX `EventType` (e.g. `dns-cache-poisoning`) into a
 * human-readable label (e.g. `DNS Cache Poisoning`).
 */
export const formatEventType = (type: string): string =>
  type
    .split('-')
    .map((word) =>
      EVENT_TYPE_ACRONYMS.has(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');

/** Metis filter limiting incidents to a single supplier (company). */
export const buildSupplierFilter = (supplierId: number) => `companies.id:(${supplierId})`;

/**
 * Metis filter limiting incidents to any of a dimension's `types`. Dimensions
 * with no mapped event types return a filter that cannot match any incident,
 * so the tab renders an empty findings table.
 */
export const buildCategoryFilter = (incidentTypes: readonly string[]) =>
  incidentTypes.length === 0
    ? 'id:(-1)'
    : `types:(${incidentTypes.map((type) => `"${type}"`).join(' OR ')})`;

/** Per-client incident statuses selectable in the supplier findings filter. */
export const SUPPLIER_INCIDENT_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
] as const;

/** Assignee filter sentinel matching incidents with no assignee for the client. */
export const UNASSIGNED_VALUE = '__unassigned__';

/**
 * Metis filter matching the effective per-client status shown in the table. The
 * displayed status is resolved for the active client only (see
 * `getEffectiveClientStatus`), so the clauses are scoped by `client_id`:
 *
 * - `acknowledged` / `resolved` — an entry for the client with that status.
 * - `new` — no client entry carries an acknowledged/resolved status (covering
 *   both "no entry for this client" and an explicit `new`), mirroring the
 *   default applied in the UI.
 *
 * Returns `null` when nothing is selected (no clause to add).
 */
export const buildClientStatusFilter = (
  statuses: readonly string[],
  clientId: number | null
): string | null => {
  if (!statuses.length || clientId == null) return null;

  const clause = (status: string) =>
    status === 'new'
      ? `(NOT client_state:{client_id:${clientId} AND status:(acknowledged OR resolved)})`
      : `client_state:{client_id:${clientId} AND status:${status}}`;

  const clauses = statuses.map(clause);
  return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`;
};

/**
 * Metis filter matching the effective per-client assignee shown in the table.
 * Scoped by `client_id` to mirror `getEffectiveAssignee`. The
 * {@link UNASSIGNED_VALUE} sentinel matches incidents with no assignee for the
 * active client. Returns `null` when nothing is selected.
 */
export const buildAssigneeFilter = (
  assignees: readonly string[],
  clientId: number | null
): string | null => {
  if (!assignees.length || clientId == null) return null;

  const ids = assignees.filter((value) => value !== UNASSIGNED_VALUE);
  const includeUnassigned = assignees.includes(UNASSIGNED_VALUE);

  const clauses: string[] = [];
  if (ids.length) {
    clauses.push(`client_state:{client_id:${clientId} AND assignee.id:(${ids.join(' OR ')})}`);
  }
  if (includeUnassigned) {
    clauses.push(`(NOT client_state:{client_id:${clientId} AND assignee:*})`);
  }

  if (!clauses.length) return null;
  return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`;
};
